import { useState } from 'react';
import type { InterestOutcome, Match, PendingInterest } from '@sfc/sdk';
import { availabilityLabel, collaborationLabel, likedLabel } from '../vocabulary';
import type { InboxState } from './useInterest';

/**
 * Who liked your ideas, and the one decision that un-anonymises anybody.
 *
 * Each card says what the person does and how much time they have, and
 * nothing that says who they are -- because the response has no field for it.
 * Accepting is the moment that changes, and it is worth marking: the name
 * arrives in the answer and is shown straight away rather than being left to
 * be discovered on another screen.
 */
export function InterestScreen({
  state,
  answer,
  onSeeMatches,
}: {
  state: InboxState;
  answer: (interestId: string, outcome: InterestOutcome) => Promise<Match | undefined>;
  onSeeMatches: () => void;
}): React.JSX.Element {
  const [matched, setMatched] = useState<Match>();
  const [deciding, setDeciding] = useState<string>();

  async function decide(interestId: string, outcome: InterestOutcome) {
    setDeciding(interestId);
    const match = await answer(interestId, outcome);
    setDeciding(undefined);
    if (match) setMatched(match);
  }

  if (state.status === 'loading') {
    return (
      <p className="loading" data-testid="interest-loading">
        Looking…
      </p>
    );
  }

  if (state.status === 'unreachable') {
    return (
      <div className="placeholder">
        <h2>We cannot reach the server</h2>
        <p className="notice notice--problem" role="status">
          Nobody has been lost. Try again in a moment.
        </p>
      </div>
    );
  }

  return (
    <section className="column" aria-label="People waiting on your ideas">
      {matched && (
        <div className="matched" role="status" data-testid="just-matched">
          <h2>You matched with {matched.person.displayName}</h2>
          <p className="fine">
            They have been told too. Neither of you has the other's email address: the conversation
            happens here.
          </p>
          <div className="matched__actions">
            <button className="button button--primary" type="button" onClick={onSeeMatches}>
              See your matches
            </button>
            <button className="button button--quiet" type="button" onClick={() => setMatched(undefined)}>
              Keep going
            </button>
          </div>
        </div>
      )}

      {state.problem && (
        <p className="notice notice--problem" role="status" data-testid="interest-problem">
          {state.problem}
        </p>
      )}

      {state.ideas.length === 0 ? (
        <div className="placeholder" data-testid="interest-empty">
          <h2>Nobody is waiting</h2>
          <p>
            When somebody likes one of your ideas they appear here, and you decide whether to
            introduce yourselves.
          </p>
          <p className="fine">
            They will not know you looked, and you will not know who they are until you accept.
          </p>
        </div>
      ) : (
        state.ideas.map((idea) => (
          <article key={idea.ideaId} className="group" data-testid="interest-group">
            <header className="group__head">
              <h2 className="group__title">{idea.title}</h2>
              <p className="fine">
                {idea.pending.length === 1 ? '1 person' : `${idea.pending.length} people`} waiting
                {idea.status === 'withdrawn' && ' · you withdrew this one'}
                {idea.status === 'removed' && ' · we took this one down'}
              </p>
            </header>

            <ul className="people">
              {idea.pending.map((person) => (
                <li key={person.id}>
                  <Waiting
                    person={person}
                    busy={deciding === person.id}
                    onDecide={(outcome) => void decide(person.id, outcome)}
                  />
                </li>
              ))}
            </ul>
          </article>
        ))
      )}
    </section>
  );
}

function Waiting({
  person,
  busy,
  onDecide,
}: {
  person: PendingInterest;
  busy: boolean;
  onDecide: (outcome: InterestOutcome) => void;
}): React.JSX.Element {
  const wrote = person.availability || person.collaborationType || person.skills.length > 0;

  return (
    <div className="person person--waiting" data-testid="waiting-person">
      <p className="fine person__when">{likedLabel(person.likedAt)}</p>

      <div className="person__facts">
        {person.availability && <span className="chip">{availabilityLabel(person.availability)}</span>}
        {person.collaborationType && (
          <span className="chip">{collaborationLabel(person.collaborationType)}</span>
        )}
      </div>

      {/* The same line a deck card has, and for the same reason: above it is
          who they are to work with, below it is what they can do. */}
      <div className="card__seam" aria-hidden="true" />

      {person.skills.length > 0 ? (
        <>
          <h3 className="card__label">What they do</h3>
          <ul className="skills">
            {person.skills.map((skill) => (
              <li key={skill.id} className="chip chip--skill">
                {skill.label}
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="fine">
          {wrote ? 'No skills chosen yet.' : 'They have not filled anything in yet.'}
        </p>
      )}

      <div className="choices">
        <button
          className="button button--pass"
          type="button"
          disabled={busy}
          data-testid="pass-person"
          onClick={() => onDecide('passed')}
        >
          Pass
        </button>
        <button
          /* Volt, like the deck's own yes. Accepting is the same answer from
             the other side of the product, and it should look like it. */
          className="button button--interested"
          type="button"
          disabled={busy}
          data-testid="accept-person"
          onClick={() => onDecide('accepted')}
        >
          Accept
        </button>
      </div>
    </div>
  );
}
